// SpecialSession/useParticipantMemberOptions.ts
import { useEffect, useState } from 'react';
import { useFacilitatorPortalService, FacilitatorParticipantView } from '@actschurch/ccms-sdk';
import type { FieldOption } from '@/components/advanced/FormBuilder/types/BuilderField';

/**
 * ⚠️ `FacilitatorParticipantView` is "name and contact only" and doesn't carry `memberID` yet. Until
 * the facilitator-portal participants endpoint returns it, every participant is skipped here and
 * 'member' questions show an empty dropdown. Once the API adds it, this works with no further change.
 */
type ParticipantWithMemberID = FacilitatorParticipantView & { memberID?: number | null };

/** The registration's participants as choices for 'member' questions (value = memberID as a string). */
export function useParticipantMemberOptions(registrationID: number): FieldOption[] {
  const portalService = useFacilitatorPortalService();
  const [options, setOptions] = useState<FieldOption[]>([]);

  useEffect(() => {
    if (!registrationID) return;
    let cancelled = false;
    portalService
      .participants(registrationID)
      .then((res) => {
        if (cancelled) return;
        setOptions(
          ((res ?? []) as ParticipantWithMemberID[])
            .filter((p) => p.memberID != null)
            .map((p) => ({
              value: String(p.memberID),
              label: p.fullName || [p.name, p.surname].filter(Boolean).join(' ') || `Member #${p.memberID}`,
            })),
        );
      })
      .catch((e) => console.error('[useParticipantMemberOptions] failed to load participants:', e));
    return () => {
      cancelled = true;
    };
  }, [portalService, registrationID]);

  return options;
}
